import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Star, ExternalLink, Check, X, Search, Sparkles } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { TOOLS, TOOL_CATEGORIES, TOP_PICKS, FAQ, type ToolCategory } from "@/content/tools";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { faqSchema } from "@/lib/schema";

const seo = buildSeo({
  title: "Tamal's SEO Toolkit — 30+ Best SEO Tools Reviewed (2026)",
  description:
    "An honest, expert-curated directory of the best SEO tools — keyword research, technical SEO, rank tracking, AI content, link building, local SEO, AEO/GEO. Pros, cons, pricing, and who each tool is for.",
  path: "/seo-tools",
    ogEyebrow: "SEO Tools",
  type: "website",
  keywords:
    "best SEO tools, SEO tools 2026, Ahrefs vs Semrush, free SEO tools, AEO tools, AI search visibility, technical SEO tools, local SEO tools, rank trackers",
});

export const Route = createFileRoute("/seo-tools")({
  head: () => ({ meta: seo.meta, links: seo.links }),
  component: SeoToolsPage,
});

function Stars({ value }: { value: number }) {
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={13}
          className={
            i <= Math.round(value)
              ? "fill-primary text-primary"
              : "text-muted-foreground/40"
          }
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">{value.toFixed(1)}</span>
    </div>
  );
}

function SeoToolsPage() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<ToolCategory | "All">("All");
  const [pricing, setPricing] = useState<"All" | "Free" | "Freemium" | "Paid">("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TOOLS.filter((t) => {
      if (activeCat !== "All" && t.category !== activeCat) return false;
      if (pricing !== "All" && t.pricing !== pricing) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.tagline.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    });
  }, [query, activeCat, pricing]);

  const topPicks = TOOLS.filter((t) => TOP_PICKS.includes(t.slug));

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Tamal's SEO Toolkit",
    itemListElement: TOOLS.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "SoftwareApplication",
        name: t.name,
        applicationCategory: "SEOApplication",
        operatingSystem: "Web",
        description: t.tagline,
        offers: { "@type": "Offer", price: t.pricing === "Free" ? "0" : undefined, priceCurrency: "USD" },
        url: t.affiliate,
      },
    })),
  };

  const faqLd = faqSchema(FAQ);

  const crumbs = breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "SEO Tools", path: "/seo-tools" },
  ]);

  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "SEO Tools", path: "/seo-tools" }]} />
      <JsonLd data={itemListSchema} />
      <JsonLd data={faqLd} />
      <JsonLd data={crumbs} />

      {/* Hero */}
      <Section headingLevel={1}
        eyebrow="The Toolkit"
        title={
          <>
            Tamal's <span className="text-gradient">SEO Toolkit</span>
          </>
        }
        subtitle={
          <>
            {TOOLS.length} tools organised by purpose. Check current provider details before choosing a plan.
            Honest pros, cons, pricing, and who each tool is for. Some links are affiliate, none are sponsored.
          </>
        }
      >
        {/* Search + filters */}
        <Reveal>
          <div className="rounded-3xl glass p-5 md:p-6">
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-background/40 px-4 py-3">
              <Search size={16} className="text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by tool name, category, or use case…"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                aria-label="Search SEO tools"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-xs text-muted-foreground hover:text-foreground">
                  Clear
                </button>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {(["All", ...TOOL_CATEGORIES] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCat(c as ToolCategory | "All")}
                  className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] transition ${
                    activeCat === c
                      ? "bg-primary text-primary-foreground"
                      : "border border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {(["All", "Free", "Freemium", "Paid"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPricing(p)}
                  className={`px-3 py-1 rounded-full text-[11px] tracking-[0.15em] uppercase transition ${
                    pricing === p
                      ? "bg-foreground text-background"
                      : "border border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {p}
                </button>
              ))}
              <span className="ml-auto self-center text-xs text-muted-foreground">
                Showing {filtered.length} of {TOOLS.length}
              </span>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* Top picks */}
      <Section eyebrow="Editor's picks" title="If I could only keep 5 tools">
        <Reveal>
          <div className="mb-8 rounded-2xl border border-border/70 bg-secondary/40 p-5 flex items-start gap-3">
            <Sparkles size={18} className="text-primary mt-0.5 flex-none" />
            <p className="text-sm text-muted-foreground">
              Many of these tools — especially the AEO/GEO and schema-focused ones —
              are the same stack I use to earn ChatGPT, Perplexity, and Gemini citations for clients.{" "}
              <Link
                to="/services/aeo-geo"
                className="text-foreground underline underline-offset-4 decoration-primary/60 hover:decoration-primary"
              >
                See the AEO &amp; GEO service →
              </Link>
            </p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {topPicks.map((t, i) => (
            <Reveal key={t.slug} delay={i * 0.05}>
              <a
                href={t.affiliate}
                target="_blank"
                rel="sponsored noopener noreferrer"
                className="group block h-full rounded-3xl glass p-5 hover:shadow-[var(--shadow-glow)] transition"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-[0.18em] text-primary">{t.category}</div>
                  <ExternalLink size={14} className="opacity-50 group-hover:opacity-100 transition" />
                </div>
                <div className="mt-3 font-display text-2xl">{t.name}</div>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{t.tagline}</p>
                <div className="mt-4 flex items-center justify-between">
                  <Stars value={t.rating} />
                  <span className="text-xs text-muted-foreground">{t.pricing}</span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Full directory */}
      <Section eyebrow="The full directory" title="Every tool, reviewed honestly">
        {filtered.length === 0 ? (
          <div className="rounded-3xl glass p-12 text-center text-muted-foreground">
            No tools match those filters. Try clearing them.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((t, i) => (
              <article
                key={t.slug}
                id={t.slug}
                className="rounded-3xl glass p-6 flex flex-col cv-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-primary">{t.category}</div>
                    <h3 className="mt-1 font-display text-2xl leading-tight">{t.name}</h3>
                  </div>
                  {t.badge && (
                    <span className="shrink-0 rounded-full border border-primary/40 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-primary">
                      {t.badge}
                    </span>
                  )}
                </div>

                <p className="mt-3 text-sm text-muted-foreground">{t.tagline}</p>

                <div className="mt-4 flex items-center justify-between text-xs">
                  <Stars value={t.rating} />
                  <span className="text-muted-foreground">
                    {t.pricing}
                    {t.startsAt ? ` · ${t.startsAt}` : ""}
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-foreground font-medium mb-1">Pros</div>
                    <ul className="space-y-1">
                      {t.pros.slice(0, 3).map((p) => (
                        <li key={p} className="flex gap-1.5 text-muted-foreground">
                          <Check size={12} className="mt-0.5 shrink-0 text-primary" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-foreground font-medium mb-1">Cons</div>
                    <ul className="space-y-1">
                      {t.cons.slice(0, 3).map((c) => (
                        <li key={c} className="flex gap-1.5 text-muted-foreground">
                          <X size={12} className="mt-0.5 shrink-0 opacity-70" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-4 text-xs text-muted-foreground">
                  <span className="text-foreground">Best for:</span> {t.bestFor}
                </div>

                <a
                  href={t.affiliate}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                  className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition"
                >
                  Try {t.name}
                  <ExternalLink size={14} />
                </a>
              </article>
            ))}
          </div>
        )}
      </Section>

      {/* FAQ */}
      <Section eyebrow="FAQ" title="SEO tool questions, answered">
        <div className="grid md:grid-cols-2 gap-5">
          {FAQ.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.05}>
              <div className="rounded-3xl glass p-6">
                <h3 className="font-display text-xl">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 rounded-3xl border border-border/60 bg-card/40 p-5 text-xs text-muted-foreground">
          <strong className="text-foreground">Affiliate disclosure:</strong> This page contains affiliate links. If you
          sign up through them I may earn a commission at no additional cost to you. I only recommend tools I personally
          use or have rigorously tested with clients — never sponsored placements.
        </div>
      </Section>
    </>
  );
}